import { useEffect, useRef, useState } from 'react'
import { useStore } from '../core/store'
import { recoveryDocumentsJson, retryDurablePersistence } from '../core/autosave'

type SaveState = ReturnType<typeof useStore.getState>['saveState']

/**
 * How long a write may be in flight before the screen says so.
 *
 * "Saving changes…" used to stand whenever a save was merely SCHEDULED, and the
 * autosave schedules one on every change and waits for a lull — so through the
 * worked example's unfurl, which changes the graph on every beat, it stood for 20
 * of a newcomer's first 21 seconds, over an example they had not made. A write
 * itself takes a fraction of that. So the notice waits for a write to be slow,
 * which is the one case where the person has anything to wait for, and says
 * nothing about the routine. A failure is never delayed: that is the notice with
 * something to do in it.
 *
 * This changes what is ANNOUNCED and nothing about what is saved — the autosave,
 * its debounce, its ceiling and the durable boundary are exactly as they were.
 */
export const SAVE_NOTICE_DELAY_MS = 1500

/** What the corner says, on the state and how long the current write has run. */
export function saveAnnouncement(state: SaveState, inFlightMs: number): 'failed' | 'slow' | null {
  if (state === 'failed') return 'failed'
  if (state === 'saving' && inFlightMs >= SAVE_NOTICE_DELAY_MS) return 'slow'
  return null
}

/**
 * Whether leaving the page should ask first: only while a save is pending or in
 * flight, which is the window in which closing the tab can lose an edit the
 * screen shows. The `pagehide` checkpoint still runs either way; this is the
 * browser's own "leave site?" prompt, and it is held up only while it is true.
 * Never at rest, because a prompt on every close teaches people to click through
 * it — and a listener left on for good costs the page its back-forward cache.
 */
export const guardsUnload = (state: SaveState): boolean => state === 'pending' || state === 'saving'

/**
 * What a press of "Retry saving" came to, from the save state it left and
 * whether it threw.
 *
 * The press used to swallow every rejection: a retry before storage ever
 * opened throws, and nothing was said, so the one control on the one screen where
 * work is at risk could produce nothing at all — the same notice, the same
 * words. Now the notice says so, and points at the copy that cannot fail (D335).
 */
export const retryOutcome = (after: SaveState, threw: boolean): 'idle' | 'still' =>
  threw || after === 'failed' ? 'still' : 'idle'

/** Said under the message when a retry did not take. */
export const STILL_FAILING = 'Still not saving. Export a recovery copy so nothing is lost.'

/** Recovery reads the in-memory documents, including unsaved outgoing work. */
export function DurabilityNotice() {
  const state = useStore((s) => s.saveState)
  const message = useStore((s) => s.saveError)
  const [retry, setRetry] = useState<'idle' | 'trying' | 'still'>('idle')
  const exportBtn = useRef<HTMLButtonElement | null>(null)
  // A notice that clears and comes back later is a new failure, not this retry's.
  useEffect(() => {
    if (state !== 'failed') setRetry('idle')
  }, [state])
  // Focus goes to the one press left that cannot fail, once the retry says so.
  useEffect(() => {
    if (retry === 'still') exportBtn.current?.focus()
  }, [retry])
  /** Set once the write in flight has run past the delay; reset by any change. */
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    setSlow(false)
    if (state !== 'saving') return
    const t = window.setTimeout(() => setSlow(true), SAVE_NOTICE_DELAY_MS)
    return () => window.clearTimeout(t)
  }, [state])

  const guard = guardsUnload(state)
  useEffect(() => {
    if (!guard) return
    const ask = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // The legacy half of the same request, for engines that still read it.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', ask)
    return () => window.removeEventListener('beforeunload', ask)
  }, [guard])

  const said = saveAnnouncement(state, slow ? SAVE_NOTICE_DELAY_MS : 0)
  if (said === 'slow')
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          bottom: 12,
          left: 12,
          zIndex: 10000,
          padding: '6px 10px',
          borderRadius: 4,
          background: 'var(--surface-solid)',
          color: 'var(--text-2)',
          fontSize: 12,
          pointerEvents: 'none',
        }}
      >
        Saving…
      </div>
    )
  if (said !== 'failed') return null
  const download = () => {
    const url = URL.createObjectURL(
      new Blob([recoveryDocumentsJson()], { type: 'application/json' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'pathways-recovery.json'
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  return (
    <div
      role="alert"
      className="durability-notice"
      style={{
        position: 'fixed',
        bottom: 12,
        left: 12,
        right: 12,
        zIndex: 10000,
        padding: 16,
        background: 'var(--surface-solid)',
        color: 'var(--text-1)',
        border: '1px solid var(--accent)',
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      <span style={{ flex: '1 1 250px' }}>
        {message}
        {retry === 'still' && (
          <>
            <br />
            {STILL_FAILING}
          </>
        )}
      </span>
      {/* Disabled and relabelled while the write is out, so a press shows it was
          taken inside the frame (the Save button's "Checking the key…" pattern). */}
      <button
        className="btn btn--outline"
        disabled={retry === 'trying'}
        onClick={() => {
          setRetry('trying')
          retryDurablePersistence().then(
            () => setRetry(retryOutcome(useStore.getState().saveState, false)),
            () => setRetry(retryOutcome(useStore.getState().saveState, true)),
          )
        }}
      >
        {retry === 'trying' ? 'Retrying…' : 'Retry saving'}
      </button>
      <button ref={exportBtn} className="btn btn--primary" onClick={download}>
        Export recovery copy
      </button>
    </div>
  )
}
